import { useState } from 'react'
import { useAccountStore } from '../stores/accountStore'
import type { DataSource } from '../types'
import { cn, formatRelativeTime } from '../lib/utils'

function AccountManager() {
  const { accounts, addAccount, deleteAccount, toggleAccount } = useAccountStore()

  const [newUsername, setNewUsername] = useState('')
  const [newDataSource, setNewDataSource] = useState<DataSource>('rss')
  const [isAdding, setIsAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newUsername.trim()) return

    setIsAdding(true)
    setError(null)

    const result = await addAccount({
      username: newUsername.trim().replace(/^@/, ''),
      data_source: newDataSource,
    })

    if (!result.success) {
      setError(result.error || 'Failed to add account')
    } else {
      setNewUsername('')
    }

    setIsAdding(false)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this account?')) return
    await deleteAccount(id)
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        Account Management
      </h2>

      {/* Add Account Form */}
      <form onSubmit={handleAdd} className="card p-4 mb-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
          Add New Account
        </h3>

        {error && (
          <div className="p-3 mb-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Username
            </label>
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder="@username"
              className="input"
            />
          </div>

          <div className="w-40">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Data Source
            </label>
            <select
              value={newDataSource}
              onChange={(e) => setNewDataSource(e.target.value as DataSource)}
              className="input"
            >
              <option value="rss">RSS (Free)</option>
              <option value="xapi">X API</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              disabled={isAdding || !newUsername.trim()}
              className={cn(
                'btn-primary',
                (isAdding || !newUsername.trim()) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {isAdding ? 'Adding...' : 'Add Account'}
            </button>
          </div>
        </div>

        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          RSS uses free Nitter proxies. X API requires a bearer token in settings.
        </p>
      </form>

      {/* Account List */}
      <div className="card divide-y divide-gray-200 dark:divide-gray-700">
        {accounts.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <svg
              className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            No accounts added yet. Add your first account above!
          </div>
        ) : (
          accounts.map((account) => (
            <div
              key={account.id}
              className="p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                {/* Avatar */}
                <div
                  className={cn(
                    'w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold',
                    account.enabled
                      ? 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                  )}
                >
                  {account.display_name?.[0]?.toUpperCase() ||
                    account.username[0].toUpperCase()}
                </div>

                {/* Info */}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {account.display_name || account.username}
                    </span>
                    <a
                      href={`https://twitter.com/${account.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
                    >
                      @{account.username}
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                    <span
                      className={cn(
                        'px-2 py-0.5 rounded text-xs',
                        account.data_source === 'xapi'
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                          : 'bg-gray-100 dark:bg-gray-700'
                      )}
                    >
                      {account.data_source === 'xapi' ? 'X API' : 'RSS'}
                    </span>
                    {account.last_fetched_at && (
                      <span>
                        Last fetched: {formatRelativeTime(account.last_fetched_at)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleAccount(account.id)}
                  className={cn(
                    'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                    account.enabled
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  )}
                >
                  {account.enabled ? 'Enabled' : 'Disabled'}
                </button>

                <button
                  onClick={() => handleDelete(account.id)}
                  className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                  title="Delete account"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default AccountManager
